import { onUnmounted } from "vue";

import type { StickyEnlargeTrigger } from "@/model/action";
import type { Board } from "@/model/board";
import type { Card } from "@/model/card";
import type { ServerTime } from "@/model/timer";

import EventBus from "./EventBus";

export const sendBoardSwitch = () => sendEvent("boardSwitch", null);
export const sendSetting = (name: string, value: unknown) => sendEvent("setting", { name, value });
export const sendServerTick = (serverTime: ServerTime) => sendEvent("serverTick", serverTime);
export const sendBoardLoaded = (boardId: Board["id"]) => sendEvent("boardLoaded", boardId);
export const sendWsReconnect = () => sendEvent("wsReconnect", null);

// ------------------------------ New sticky note actions ------------------------------

interface StickyNoteActionBase<T extends string> {
  action: T;
  id: Card["id"];
}

export type StickyNoteEnlarge = StickyNoteActionBase<"enlarge"> & {
  focus?: boolean;
  trigger: StickyEnlargeTrigger;
};
export type StickyNoteShrink = StickyNoteActionBase<"shrink">;
export type StickyNoteFocus = StickyNoteActionBase<"focus">;
type StickyNoteChanged = StickyNoteActionBase<"changed">;
type StickyNoteDeactivate = StickyNoteActionBase<"deactivate">;

type StickyNoteAction =
  | StickyNoteEnlarge
  | StickyNoteShrink
  | StickyNoteFocus
  | StickyNoteChanged
  | StickyNoteDeactivate;

type StickyNoteActionPayloads = {
  [K in StickyNoteAction["action"]]: Omit<Extract<StickyNoteAction, { action: K }>, "id">;
};

export const sendStickyNoteAction = <T extends StickyNoteAction["action"]>(
  id: Card["id"],
  action: StickyNoteActionPayloads[T],
) => {
  sendEvent("stickyNoteAction", { id, ...action } as StickyNoteAction);
};

export const emitStickyChanged = (id: Card["id"]) =>
  sendStickyNoteAction(id, { action: "changed" });

interface EventPayloads {
  boardSwitch: null;
  setting: { name: string; value?: unknown };
  serverTick: ServerTime;
  boardLoaded: Board["id"];
  wsReconnect: null; // Used to fetch missing events after a reconnect
  stickyNoteAction: StickyNoteAction;
}

type EventType = keyof EventPayloads;

function sendEvent<T extends EventType>(name: T, data: EventPayloads[T]): void {
  EventBus.emit(name, data);
}

// neverUnregister must be true when not called from a vue component because onUnmounted
export function useEventBus(opt: { neverUnregister?: boolean } = {}) {
  if (!opt?.neverUnregister) {
    onUnmounted(unregister);
  }

  const unsubscribes = new Array<() => void>();

  function unregister() {
    for (const unsubscribe of unsubscribes) {
      unsubscribe();
    }
    unsubscribes.length = 0;
  }

  function onBoardSwitch(callback: () => void) {
    onEvent("boardSwitch", callback);
  }

  function onServerTick(callback: (serverTime: ServerTime) => void) {
    onEvent("serverTick", callback);
  }

  function onStickyNoteAction<T extends StickyNoteAction["action"]>(
    id: Card["id"] | null,
    action: T,
    callback: (action: Extract<StickyNoteAction, { action: T }>) => void | Promise<void>,
  ) {
    onEvent("stickyNoteAction", (e: StickyNoteAction) => {
      if (id === null && e.action === action) {
        void callback(e as Extract<StickyNoteAction, { action: T }>);
        return;
      }

      if (e.id === id && e.action === action) {
        void callback(e as Extract<StickyNoteAction, { action: T }>);
      }
    });
  }

  function onSetting(name: string, callback: (e: unknown) => void) {
    onEvent("setting", (e) => {
      if (e.name === name) {
        callback(e.value);
      }
    });
  }

  function onBoardLoaded(callback: (boardId: Board["id"]) => void | Promise<void>) {
    onEvent("boardLoaded", (boardId: Board["id"]) => void callback(boardId));
  }

  function onReconnect(callback: () => void) {
    onEvent("wsReconnect", callback);
  }

  function onEvent<T extends EventType>(name: T, action: (e: EventPayloads[T]) => void) {
    unsubscribes.push(() => EventBus.off(name, action));
    EventBus.on(name, action);
  }

  return { onBoardSwitch, onServerTick, onStickyNoteAction, onSetting, onBoardLoaded, onReconnect };
}
